import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Download, Heart, XCircle } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { useCart } from "@/components/CartProvider";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState(null);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep trying */ }
      polls.current += 1;
      if (polls.current >= 10) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const isDonation = info?.category === "donation" || info?.category === "patron";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-20" data-testid="payment-success-page">
      <div className="max-w-lg w-full bg-white rounded-3xl p-10 card-tactile text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-bsn-gold animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-2xl font-semibold text-bsn-midnight">Confirming your payment…</h1>
            <p className="text-bsn-sapphire/75 mt-3">One gentle moment while we verify everything securely.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h1 className="font-serif text-3xl font-bold text-bsn-midnight" data-testid="success-heading">
              {isDonation ? "Thank you, beacon of hope" : "Your purchase is complete"}
            </h1>
            <p className="text-bsn-sapphire/80 mt-4 leading-relaxed">
              {isDonation
                ? "Your generosity keeps crisis resources free and places courage in someone's hands tonight. 💛"
                : "A confirmation and your download links are on their way to your email. Your printable resources are ready."}
            </p>
            {info?.product_name && <p className="mt-4 font-serif text-lg text-bsn-midnight">{info.product_name}</p>}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              {!isDonation && (
                <Link to="/shop" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold" data-testid="success-shop-link">
                  <Download className="w-5 h-5" /> Back to Shop
                </Link>
              )}
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-bsn-midnight text-bsn-parchment font-semibold hover:bg-bsn-navy transition-colors" data-testid="success-home-link">
                <Heart className="w-5 h-5" /> Return Home
              </Link>
            </div>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="font-serif text-2xl font-semibold text-bsn-midnight">We couldn't confirm this payment</h1>
            <p className="text-bsn-sapphire/80 mt-3">If you were charged, it may still be processing. Please check your email or try again.</p>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold" data-testid="failed-retry-link">
              Return to Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
